<template>

  <div id="create-menu" class="ai-importer" v-if="user_confirmed">

    <h2>Create a menu</h2>

    <ErrorNotice v-if="error != ''" :message="error" />

    <p class="explainer">Give your menu a name. If you have your menu as photos or a PDF, add them and we'll read the items, prices and currency for you to review before anything is added.</p>

    <label class="field-label" for="menu-name">Menu name</label>
    <input id="menu-name" type="text" class="menu-name-input" v-model="name" placeholder="e.g. Dinner menu" maxlength="40" @keyup.enter="createMenu()" />

    <p class="field-label upload-label">Upload your menu <span class="optional">(optional)</span></p>
    <MenuFilePicker v-model="files" :disabled="creating" @error="error = $event" />

    <label class="apply-styles" v-if="files.length > 0">
      <input type="checkbox" v-model="import_styles" />
      <span><strong>Import styles</strong>: also read your menu's fonts, colours, sizes and dividers, and apply them to the new menu. You'll see a preview first.</span>
    </label>


    <button @click="createMenu()" :disabled="creating || name.trim() === ''">
      {{ creating ? 'Creating…' : (files.length > 0 ? 'Create menu and read items' : 'Create menu') }}
    </button>

    <p class="legacy-link" @click="useLegacyCreator()">Use the legacy menu creator</p>

  </div>

</template>


<script>
import ErrorNotice from './ErrorNotice'
import signedInOnly from './signedInOnly'
import MenuFilePicker from './MenuFilePicker'
import { setPendingImport } from './pendingImport'

// Single-page menu creation. A menu name is all that's needed; an
// optional upload is handed to the import screen, which reads the items and currency for review.
export default {
  name: 'CreateMenu',
  components: { ErrorNotice, MenuFilePicker },
  mixins: [signedInOnly],
  data() {
    return {
      name: '',
      files: [],
      import_styles: true,
      creating: false,
      error: ''
    }
  },
  methods: {
    onUserConfirmed() {},
    menuId() {
      return null
    },
    // Not signed in: fall back to the legacy creator.
    redirectToMenu() {
      this.useLegacyCreator()
    },
    useLegacyCreator() {
      this.$router.replace('/new')
    },
    createMenu() {
      const name = this.name.trim()
      if (name === '' || this.creating) {
        return
      }
      this.error = ''
      this.creating = true
      this.$http.secured.post('/app_create_menu', {
        menu: { name, restaurant_name: name, menu_builder_v2: true, en: true, currency: 'Not specified' }
      })
        // Menus made here start published. The publish endpoint applies the usual subscription rules, so
        // without a plan that allows it the menu simply stays unpublished.
        .then(response => this.$http.secured.post(`/app_publish_menu/${response.data.menu_id}`)
          .catch(() => null)
          .then(() => response))
        .then(response => {
          const menuId = response.data.menu_id
          if (this.files.length > 0) {
            setPendingImport(menuId, this.files, this.import_styles)
            this.$router.replace(`/photo_import/${menuId}`)
          } else {
            this.$router.replace(`/${menuId}`)
          }
        })
        .catch(error => {
          this.creating = false
          const data = (error.response && error.response.data) || {}
          const messages = Object.values(data).flat().filter(message => typeof message === 'string')
          this.error = messages[0] || "Couldn't create the menu. Please try again."
        })
    }
  }
}
</script>


<style scoped src="./importer.css"></style>

<style scoped>

.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin: 30px 0px 8px 0px;
}

.upload-label {
  margin-bottom: 0px;
}

.optional {
  font-weight: 400;
  color: dimgray;
}

.menu-name-input {
  width: 100%;
  font-size: 18px;
  padding: 8px 0px;
}

.apply-styles {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  font-size: 13px;
  cursor: pointer;
  margin: 20px 0px;
}

.apply-styles input {
  margin: 3px 10px 0px 0px;
  flex-shrink: 0;
}

button {
  margin-top: 30px;
}

button:disabled {
  opacity: 0.5;
  cursor: default;
}

.legacy-link {
  font-size: 12.5px;
  font-weight: 600;
  color: dimgray;
  text-decoration: underline;
  cursor: pointer;
  width: max-content;
  margin-top: 40px;
}

</style>
